import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import api from '../api';
import {
    IMPERSONATION_LS_KEYS,
    beginImpersonation,
    clearImpersonation,
    enforceImpersonationTtlOnBoot,
    isImpersonating,
    revertToAdmin,
    setImpersonatedUserId,
} from '../services/impersonation';

// Must run before initialState reads localStorage below: an impersonation that ran out
// while the tab was closed has to be reverted to the admin session *before* the app
// boots into it. This module is the first thing to touch those keys, so this is the
// only place the ordering is guaranteed.
enforceImpersonationTtlOnBoot();

export const getApiInformation = createAsyncThunk('auth/api', async (dispatch) => {
    const response = await api.get('/');
    return response.data;
});

export const login = createAsyncThunk('auth/login', async ({ username, password, twoFactorCode, isBackupCode, deviceId, deviceName, trustDevice }, { rejectWithValue }) => {
    try {
        const response = await api.post('/auth/login', { 
            username, 
            password, 
            twoFactorCode, 
            isBackupCode,
            deviceId,
            deviceName,
            trustDevice
        });
        
        // Check if 2FA is required
        if (response.data.requiresTwoFactor) {
            return rejectWithValue({ requiresTwoFactor: true, message: response.data.message });
        }
        
        // A fresh login is never an impersonated session — drop any leftovers before the
        // new tokens land, so the identity keys below are written for this account.
        clearImpersonation();
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        return response.data;
    } catch (error) {
        // No response at all (network down / CORS / DNS) or a server-side fault → unreachable
        if (!error.response || error.response.status >= 500) {
            return rejectWithValue('Server unreachable. Please try again.');
        }
        // 4xx (e.g. 401) → genuine credential failure
        return rejectWithValue(error.response?.data?.message || 'Wrong credentials');
    }
});

export const refreshToken = createAsyncThunk('auth/refreshToken', async (pinCode, { rejectWithValue }) => {
    try {
        const response = await api.post('/auth/refreshToken', { pinCode }, { refreshToken: true });
        return response.data;
    } catch (error) {
        return rejectWithValue({
            message: error.response?.data?.message || '',
            status: error.response?.status,
        });
    }
});

const PIN_REMEMBER_LS_KEYS = ['token', 'refreshToken', 'role', 'hasPinCode', '2fa-verified', '2fa-timestamp', 'pinUser', ...IMPERSONATION_LS_KEYS];

const clearPinRememberLocalStorage = () => {
    PIN_REMEMBER_LS_KEYS.forEach(key => localStorage.removeItem(key));
};

// Lightweight identity snapshot kept in localStorage so the PIN screen can greet the
// user after a reload, when the access token has expired and the in-memory user is gone.
const persistPinUserSnapshot = (user) => {
    if (!user) return;
    const snapshot = {
        firstName: user.firstName,
        lastName: user.lastName,
        profilePic: user.profilePic?.imageUrl ? { imageUrl: user.profilePic.imageUrl } : undefined,
        parentOrganization: user.parentOrganization?.name ? { name: user.parentOrganization.name } : undefined,
    };
    localStorage.setItem('pinUser', JSON.stringify(snapshot));
};

const readPinUserSnapshot = () => {
    try {
        return JSON.parse(localStorage.getItem('pinUser')) || null;
    } catch {
        return null;
    }
};

// The identity keys drive every PIN gate and the lock screen's avatar, so they must
// always describe the account whose refresh token we hold. Nothing is persisted while
// impersonating: the admin's own identity stays on record, so an expiring session can
// never end up asking them for the impersonated user's PIN.
const persistIdentity = (user) => {
    if (!user || isImpersonating()) return;
    persistPinUserSnapshot(user);
    localStorage.setItem('hasPinCode', user.hasPinCode ? 'true' : 'false');
    if (user.role) {
        localStorage.setItem('role', user.role);
    }
};

const isPinErrorMessage = (msg) => typeof msg === 'string' && /pin/i.test(msg);

export const requestImpersonation = createAsyncThunk('auth/impersonate', async ({ userId }) => {
    const response = await api.post('/auth/impersonate', { userId });
    setImpersonatedUserId(userId);
    beginImpersonation(response.data);
    return response.data;
});

// `reason` is 'manual' (the Stop button) or 'expired' (the deadline ran out). Both go
// through the same revert; only the wording the UI shows differs. The identity is not
// resolved here — the caller lands on `exitPath` and the bootstrap below refetches
// /auth/me with the restored admin token, which is the only identity worth trusting.
export const stopImpersonation = createAsyncThunk(
    'auth/stopImpersonate',
    async ({ reason = 'manual' } = {}) => revertToAdmin({ reason }),
);

export const fetchLoggedUser = createAsyncThunk('auth/fetchLoggedUser', async () => {
    const response = await api.get('/auth/me');
    return response.data;
});

// Resolves the logged-in user's display identity from the refresh token when the
// access token has expired (PIN screen on reload). No PIN, mints no tokens.
export const fetchPinIdentity = createAsyncThunk('auth/pinIdentity', async () => {
    const response = await api.get('/auth/pin-identity', { refreshToken: true });
    return response.data;
});

export const updateUser = createAsyncThunk('auth/updateUser', async (body) => {
    const response = await api.put('/auth/me', body);
    return response.data;
});

export const registerUser = createAsyncThunk('user/register', async ({ userBody }) => {
    const response = await api.post('/auth/register', userBody);
    return response.data;
});

export const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        pinUser: readPinUserSnapshot(),
        token: localStorage.getItem('token'),
        refreshToken: localStorage.getItem('refreshToken'),
        hasPinCode: localStorage.getItem('hasPinCode') === 'true',
        pinError: false,
        pinAttempts: 0,
        forceLogout: false,
        requirePinCode: false,
        requiresTwoFactor: false,
        pendingLogin: null,
        isImpersonating: isImpersonating(),
        originalUser: null,
        originalToken: localStorage.getItem('originalToken'),
        isAuthenticated: false,
        isLoading: false,
        error: null,
        createUserStatus: null,
        lastCreatedUser: null,
        createUserErrors: [],
        apiRoot: null,
    },
    reducers: {
        requirePinCode: (state, action) => {
            state.requirePinCode = true;
            state.retryRequest = action.payload;
        },
        clearPinError: (state) => {
            state.pinError = false;
        },
        resetPinAttempts: (state) => {
            state.pinAttempts = 0;
            state.pinError = false;
        },
        clearForceLogout: (state) => {
            state.forceLogout = false;
        },
        clearTwoFactorRequirement: (state) => {
            state.requiresTwoFactor = false;
            state.pendingLogin = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        logout: (state) => {
            clearPinRememberLocalStorage();
            state.user = null;
            state.pinUser = null;
            state.token = null;
            state.refreshToken = null;
            state.hasPinCode = false;
            state.pinAttempts = 0;
            state.pinError = false;
            state.requirePinCode = false;
            state.forceLogout = false;
            state.originalUser = null;
            state.originalToken = null;
            state.isAuthenticated = false;
            state.isImpersonating = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getApiInformation.pending, (state) => {
                state.apiRoot = null;
            })
            .addCase(getApiInformation.fulfilled, (state, action) => {
                state.apiRoot = action.payload;
            })
            .addCase(getApiInformation.rejected, (state) => {
                state.apiRoot = null;
            })


            .addCase(login.fulfilled, (state, action) => {
                state.token = action.payload.token;
                state.refreshToken = action.payload.refreshToken;
                state.user = action.payload.user;
                persistIdentity(action.payload.user);
                state.hasPinCode = action.payload.user?.hasPinCode ?? false;
                state.isLoading = false;
                state.isAuthenticated = true;
                state.requiresTwoFactor = false;
                state.pendingLogin = null;
                state.error = null;
            })
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                
                if (action.payload?.requiresTwoFactor) {
                    state.requiresTwoFactor = true;
                    state.pendingLogin = action.meta.arg; // Store the original login data
                    state.error = null;
                } else {
                    state.error = action.payload || action?.error?.message;
                    state.requiresTwoFactor = false;
                    state.pendingLogin = null;
                }
            })

            .addCase(refreshToken.pending, (state) => {
                state.pinError = false;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                localStorage.setItem('token', action.payload.accessToken);
                localStorage.setItem('refreshToken', action.payload.refreshToken);
                // state.token is deliberately left alone: Layout's socket effect is keyed
                // on it, and re-keying mid-session drops the instance usePlayback bound to
                // at mount, killing the player's live updates.
                state.requirePinCode = false;
                state.pinError = false;
                state.pinAttempts = 0;
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.isAuthenticated = false;
                state.pinError = true;

                const message = action.payload?.message;
                const isPinError = isPinErrorMessage(message);

                if (isPinError) {
                    state.pinAttempts += 1;
                    if (state.pinAttempts >= 3) {
                        clearPinRememberLocalStorage();
                        state.token = null;
                        state.refreshToken = null;
                        state.hasPinCode = false;
                        state.requirePinCode = false;
                        state.pinAttempts = 0;
                        state.forceLogout = true;
                    }
                } else {
                    clearPinRememberLocalStorage();
                    state.token = null;
                    state.refreshToken = null;
                    state.hasPinCode = false;
                    state.requirePinCode = false;
                    state.pinAttempts = 0;
                    state.forceLogout = true;
                }
            })

            .addCase(requestImpersonation.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(requestImpersonation.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isImpersonating = true;
                state.originalUser = state.user;
                state.originalToken = state.token;
                state.user = action.payload.user;
                state.hasPinCode = action.payload.user?.hasPinCode ?? false;
                state.token = action.payload.token;
                state.refreshToken = action.payload.refreshToken;
            })
            .addCase(requestImpersonation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action?.error?.message;
            })

            .addCase(stopImpersonation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(stopImpersonation.fulfilled, (state) => {
                state.isLoading = false;
                state.isImpersonating = false;
                // Mirror what the revert put back in localStorage — state.originalToken
                // is null after a reload, so it can't be trusted as the source.
                state.token = localStorage.getItem('token');
                state.refreshToken = localStorage.getItem('refreshToken');
                state.hasPinCode = localStorage.getItem('hasPinCode') === 'true';
                state.pinUser = readPinUserSnapshot();
                state.originalToken = null;
                state.originalUser = null;
                // Drop the impersonated user and let Layout's bootstrap resolve the admin
                // from the restored token. Anything less risks a half-swapped session.
                state.user = null;
                state.isAuthenticated = false;
            })
            .addCase(stopImpersonation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action?.error?.message;
            })

            .addCase(fetchLoggedUser.fulfilled, (state, action) => {
                state.user = action.payload;
                state.hasPinCode = action.payload?.hasPinCode ?? false;
                persistIdentity(action.payload);
                state.isAuthenticated = true;
            })

            .addCase(fetchPinIdentity.fulfilled, (state, action) => {
                state.pinUser = action.payload;
                persistPinUserSnapshot(action.payload);
            })

            .addCase(updateUser.fulfilled, (state, action) => {
                state.user = action.payload;
            })

            .addCase(registerUser.pending, (state) => {
                state.createUserStatus = 'loading';
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.createUserStatus = 'succeeded';
                state.lastCreatedUser = action.payload;
                state.error = null;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.createUserStatus = 'failed';
                state.createUserErrors = action.payload?.errors;
            })
        ;
    },
});

export const {
    logout,
    requirePinCode,
    clearPinError,
    resetPinAttempts,
    clearForceLogout,
    clearTwoFactorRequirement,
    clearError,
} = authSlice.actions;

export default authSlice.reducer;
